/**
 * BAA builder — tolerant restore of saved multi-select ("Select all that
 * apply") answers.  GR-004 P0-3.
 *
 * A multi-select clause is stored as the clause TEXT itself: policy-input
 * .blade.php renders `value="{{ $option }}"`, the browser posts that text back,
 * and CompanyBaaController::generateBaaInputs() persists the group under one
 * indexed key with the selections joined by '|'.
 *
 * Restoring therefore means string-matching long, punctuated, sometimes
 * multi-line prose back onto a freshly re-rendered option. Several things
 * legitimately perturb that string between save and reload:
 *
 *   - HTTP form encoding normalizes newlines to CRLF, so a multi-line clause
 *     returns with \r\n where the template has \n;
 *   - the value round-trips through an HTML attribute, so & " ' < > may come
 *     back as entities;
 *   - template indentation/whitespace can be reflowed;
 *   - older saves joined arrays via String(array), producing a ','-separated
 *     list, before the current '|' join existed.
 *
 * BaaGuidedWizard.reflectField() compares with `vals.indexOf(e.value)` — an
 * exact match — so any one of those perturbations left the checkbox unchecked,
 * the clause invisible in edit mode, and the clause DROPPED from the document
 * on the next save. Normalizing both sides for the comparison and then storing
 * the option's own canonical value fixes every case at once, for pre-existing
 * rows as well as new ones, with no data migration and no destructive rewrite
 * of historical answers.
 *
 * These functions are deliberately pure and DOM-optional so they can be unit
 * tested outside a browser (see tests/js/choiceMatching.test.mjs).
 */

// Entities the HTML-attribute round-trip actually produces for clause text.
const NAMED_ENTITIES = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
    '&nbsp;': ' ',
    '&#39;': "'",
    '&#039;': "'",
    '&#34;': '"',
    '&#034;': '"',
};

function decodeEntities(str) {
    if (str.indexOf('&') === -1) {
        return str;
    }

    let out = str;

    Object.keys(NAMED_ENTITIES).forEach(function (entity) {
        if (out.indexOf(entity) !== -1) {
            out = out.split(entity).join(NAMED_ENTITIES[entity]);
        }
    });

    // Numeric entities (decimal and hex) — e.g. &#8217; for a curly apostrophe.
    out = out.replace(/&#(\d+);/g, function (_m, code) {
        return String.fromCharCode(parseInt(code, 10));
    });
    out = out.replace(/&#x([0-9a-f]+);/gi, function (_m, code) {
        return String.fromCharCode(parseInt(code, 16));
    });

    return out;
}

/**
 * Reduce a clause to a form that is stable across the save/reload round-trip.
 * Case-folded and whitespace-collapsed, with entities decoded and stray markup
 * removed. Used ONLY for comparison — never stored or displayed.
 */
export function normalizeChoice(value) {
    const str = String(value === null || value === undefined ? '' : value);

    return decodeEntities(str)
        .replace(/ /g, ' ')    // non-breaking space
        .replace(/\r\n?/g, '\n')    // CRLF / CR -> LF
        .replace(/<[^>]*>/g, ' ')   // stray markup
        .replace(/\s+/g, ' ')       // collapse every run of whitespace
        .trim()
        .toLowerCase();
}

/**
 * Resolve one stored candidate onto the option it refers to, returning that
 * option's OWN value string — the thing reflectField()/v-model compare
 * against. Returns null when the candidate matches no option, so a partial or
 * junk fragment is dropped rather than checking a box at random.
 *
 * @param {Array<{value: string}>} options
 * @param {*} candidate
 * @returns {string|null}
 */
export function matchOptionValue(options, candidate) {
    const opts = options || [];
    if (!opts.length) {
        return null;
    }

    const needle = normalizeChoice(candidate);
    if (needle === '') {
        return null;
    }

    for (let i = 0; i < opts.length; i++) {
        if (normalizeChoice(opts[i].value) === needle) {
            return opts[i].value;
        }
    }

    return null;
}

/**
 * Rebuild the full selected-values array for a multi-select fill-in from every
 * storage shape this app has ever written.
 *
 * @param {Array<{value: string}>} options  the freshly-rendered option list
 * @param {Array<*>} pool                   raw stored value(s) for the group
 * @returns {Array<string>}                 canonical option values, in option order of discovery
 */
export function checkboxValuesFromStored(options, pool) {
    const out = [];

    const push = function (value) {
        if (value !== null && out.indexOf(value) === -1) {
            out.push(value);
        }
    };

    (pool || []).forEach(function (entry) {
        // A real array survived (JSON draft state, or a form array).
        if (Array.isArray(entry)) {
            entry.forEach(function (value) {
                push(matchOptionValue(options, value));
            });

            return;
        }

        const raw = String(entry === null || entry === undefined ? '' : entry);
        if (raw.trim() === '') {
            return;
        }

        // Whole-string match FIRST: a single clause may legitimately contain a
        // separator character, and must not be shredded into non-matching bits.
        const whole = matchOptionValue(options, raw);
        if (whole !== null) {
            push(whole);

            return;
        }

        // Current save format: '|'-joined (CompanyBaaController::generateBaaInputs).
        let matchedAny = false;
        raw.split('|').forEach(function (part) {
            const match = matchOptionValue(options, part);
            if (match !== null) {
                matchedAny = true;
                push(match);
            }
        });

        if (matchedAny) {
            return;
        }

        // Legacy format: String(array) produced a ','-joined list. Only
        // consulted when the '|' reading matched nothing at all, so a clause
        // containing commas is never split by mistake.
        raw.split(',').forEach(function (part) {
            push(matchOptionValue(options, part));
        });
    });

    return out;
}
